import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Button } from "./ui/button";
import { Leaf, LogOut, UserCircle2, Menu, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import LanguagePicker from "./LanguagePicker";
import CountryPill from "./shop/CountryPill";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-sm transition-colors ${
      isActive ? "text-emerald-800 font-semibold" : "text-emerald-900/70 hover:text-emerald-800"
    }`;

  return (
    <header
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-emerald-100/60"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-home-link">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-emerald-700 text-white shadow-sm group-hover:scale-105 transition">
            <Leaf className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold text-emerald-950">Feel Greats</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-600">
              Smart health · real results
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" className={linkCls} end data-testid="nav-landing">{t("header.home")}</NavLink>
          <NavLink to="/shop" className={linkCls} data-testid="nav-shop">Shop</NavLink>
          <NavLink to="/learn" className={linkCls} data-testid="nav-learn">{t("header.learn")}</NavLink>
          <MoreMenu />
          {user && user !== false ? (
            <>
              <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">{t("header.dashboard")}</NavLink>
              <NavLink to="/questionnaire" className={linkCls} data-testid="nav-assessment">{t("header.assessment")}</NavLink>
              <NavLink to="/report" className={linkCls} data-testid="nav-report">{t("header.report")}</NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" className={linkCls} data-testid="nav-admin">{t("header.admin")}</NavLink>
              )}
            </>
          ) : null}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <CountryPill variant="compact" />
          <LanguagePicker />
          {user && user !== false ? (
            <>
              <span className="flex items-center gap-2 text-sm text-emerald-900/80">
                <UserCircle2 className="w-4 h-4" /> {user.name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-full text-emerald-900 hover:bg-emerald-50"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" /> {t("common.logout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link">
                <Button variant="ghost" size="sm" className="rounded-full text-emerald-900 hover:bg-emerald-50">
                  {t("header.login")}
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-signup-link">
                <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5">
                  {t("header.start_free")}
                </Button>
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <CountryPill variant="compact" />
          <LanguagePicker />
          <button
            type="button"
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl hover:bg-emerald-50 text-emerald-900"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-emerald-100/70 bg-white/95" data-testid="mobile-nav-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            <NavLink to="/" className={linkCls} end onClick={() => setOpen(false)}>{t("header.home")}</NavLink>
            <NavLink to="/shop" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-shop">Shop</NavLink>
            <NavLink to="/learn" className={linkCls} onClick={() => setOpen(false)}>{t("header.learn")}</NavLink>
            <div className="border-t border-emerald-100/70 my-1 pt-1">
              <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-semibold">More</p>
              <NavLink to="/about" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-about">About us</NavLink>
              <NavLink to="/how-it-works" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-how">How it works</NavLink>
              <NavLink to="/why-feelgreats" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-why">Why Feel Greats</NavLink>
              <NavLink to="/mentor-support" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-mentor">Mentor support</NavLink>
              <NavLink to="/science" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-science">Science & ingredients</NavLink>
              <NavLink to="/stories" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-stories">Member stories</NavLink>
              <NavLink to="/faq" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-faq">FAQ</NavLink>
              <NavLink to="/contact" className={linkCls} onClick={() => setOpen(false)} data-testid="mobile-nav-contact">Contact</NavLink>
            </div>
            {user && user !== false ? (
              <>
                <NavLink to="/dashboard" className={linkCls} onClick={() => setOpen(false)}>{t("header.dashboard")}</NavLink>
                <NavLink to="/questionnaire" className={linkCls} onClick={() => setOpen(false)}>{t("header.assessment")}</NavLink>
                <NavLink to="/report" className={linkCls} onClick={() => setOpen(false)}>{t("header.report")}</NavLink>
                {user.role === "admin" && (
                  <NavLink to="/admin" className={linkCls} onClick={() => setOpen(false)}>{t("header.admin")}</NavLink>
                )}
                <button
                  className="text-left px-3 py-2 text-sm text-emerald-900/70 hover:text-emerald-800"
                  onClick={() => { setOpen(false); handleLogout(); }}
                  data-testid="mobile-nav-logout"
                >
                  {t("common.logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-3 py-2 text-sm text-emerald-900/70" onClick={() => setOpen(false)}>{t("header.login")}</Link>
                <Link to="/register" className="px-3 py-2 text-sm font-semibold text-emerald-800" onClick={() => setOpen(false)}>{t("header.start_free")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}


/**
 * Desktop "More ▾" dropdown for the trust pages. Keeps the primary
 * nav row to 3 links (Home / Shop / Learn) and tucks the brand-trust
 * pages under a single discoverable menu.
 */
function MoreMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const items = [
    { to: "/about", label: "About us", testid: "nav-about" },
    { to: "/how-it-works", label: "How it works", testid: "nav-how" },
    { to: "/why-feelgreats", label: "Why Feel Greats", testid: "nav-why" },
    { to: "/mentor-support", label: "Mentor support", testid: "nav-mentor" },
    { to: "/science", label: "Science & ingredients", testid: "nav-science" },
    { to: "/stories", label: "Member stories", testid: "nav-stories" },
    { to: "/faq", label: "FAQ", testid: "nav-faq" },
    { to: "/contact", label: "Contact", testid: "nav-contact" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`px-3 py-2 text-sm transition-colors inline-flex items-center gap-1 ${open ? "text-emerald-800 font-semibold" : "text-emerald-900/70 hover:text-emerald-800"}`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nav-more"
      >
        More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-56 rounded-2xl border border-emerald-100 bg-white shadow-lg py-1.5 z-50"
          data-testid="nav-more-dropdown"
        >
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              role="menuitem"
              className="block px-4 py-2 text-sm text-emerald-900/85 hover:bg-emerald-50/70 hover:text-emerald-800 transition"
              onClick={() => setOpen(false)}
              data-testid={it.testid}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
